/**
 * Acesso à api da experiência de uso (I2–I5). Mora fora de `lib/api.ts` só para não
 * disputar o fim daquele arquivo com outras frentes; a regra é a mesma: nenhuma tela fala
 * com a rede fora de um hook daqui ou de lá.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useTRPC, type ApiClient, type QueryOutput } from '../../lib/api';

export type Preferences = QueryOutput<ApiClient['experience']['preferences']['get']>;

export function usePreferences(enabled = true) {
  const trpc = useTRPC();
  return useQuery({
    ...trpc.experience.preferences.get.queryOptions(),
    enabled,
    staleTime: 5 * 60_000,
  });
}

/** Grava o retorno do servidor direto no cache: a resposta já É a preferência nova. */
function useWritePreferences() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  return (next: Preferences) =>
    queryClient.setQueryData(trpc.experience.preferences.get.queryKey(), next);
}

export function useSetDensity() {
  const trpc = useTRPC();
  const write = useWritePreferences();
  return useMutation(trpc.experience.preferences.setDensity.mutationOptions({ onSuccess: write }));
}

export function useDismissHint() {
  const trpc = useTRPC();
  const write = useWritePreferences();
  return useMutation(trpc.experience.preferences.dismissHint.mutationOptions({ onSuccess: write }));
}

export function useResetHints() {
  const trpc = useTRPC();
  const write = useWritePreferences();
  return useMutation(trpc.experience.preferences.resetHints.mutationOptions({ onSuccess: write }));
}

export function useMarkChangelogSeen() {
  const trpc = useTRPC();
  const write = useWritePreferences();
  return useMutation(
    trpc.experience.preferences.markChangelogSeen.mutationOptions({ onSuccess: write }),
  );
}

export function useSendFeedback() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  return useMutation(
    trpc.experience.feedback.send.mutationOptions({
      onSuccess: () =>
        void queryClient.invalidateQueries({
          queryKey: trpc.experience.preferences.get.queryKey(),
        }),
    }),
  );
}

/**
 * As três listas da busca (I1). MESMA chave de cache das telas de campanhas, grupos e
 * monitoradores — quem já passou por elas abre a busca com tudo pronto. Só pede com a
 * busca aberta e quando o papel pode ler.
 */
export function usePaletteSources(
  workspaceId: string,
  enabled: { campaigns: boolean; groups: boolean; monitors: boolean },
) {
  const trpc = useTRPC();
  const campaigns = useQuery({
    ...trpc.campaign.list.queryOptions({ workspaceId, includeArchived: false }),
    enabled: enabled.campaigns,
  });
  const groups = useQuery({
    ...trpc.group.list.queryOptions({ workspaceId, includeArchived: false }),
    enabled: enabled.groups,
  });
  const monitors = useQuery({
    ...trpc.monitor.list.queryOptions({ workspaceId, includeArchived: false }),
    enabled: enabled.monitors,
  });
  return { campaigns, groups, monitors };
}
